/**
 * /tsg-product-engg — TSG Nexus Product Engineering (content model).
 * (Formerly /student-intro; that path now redirects here — see
 * StudentIntroRedirect.jsx.)
 *
 * THE STORY: Learn → Build → Experience → Practice → Prove. The page shows a
 * student what their journey looks like, not a list of products.
 *
 * SECTION ORDER (StudentIntroPage.jsx composes them in this order)
 *   1 hero          Build like an engineer. Think like an engineer.
 *                   + tagline + the ecosystem orbit + the five-verb strip
 *   2 journey       Your journey goes beyond courses — the ladder of evidence
 *   3 industry      What industry expects → what TSG Nexus delivers (+ proof)
 *   4 nexus         Learn + Build inside TSG Nexus
 *   5 git           Build it. Commit it. Show it. (real GitHub repository)
 *   6 tracker       ExiTracker — experience how engineering work moves
 *   7 mock          Mock interviews — practise before the real interview
 *   8 hire          ExiHire — real-time AI hiring (never "practice")
 *   9 flow          From code to delivery: Git → ExiTracker → Mock → ExiHire
 *  10 opportunity   Top 3 performers → interview with Evolve X (not a job offer)
 *  11 referral      learn together, earn together
 *  12 confidence    confidence is built, not given (the equation)
 *  13 close         your engineering journey starts here
 *
 * ACCURACY RULES — load-bearing, do not "strengthen":
 *  · Every capability below is verified against this repository:
 *      TSG Nexus   — routes under /nextgen-builder/* (Elite Mastery, AI Tutor
 *                    explain/notes/code-review/resume-review/skill-test/
 *                    interview, mock-interview coding/technical/hr/
 *                    arithmetic/time_management, project enrollment with
 *                    GitHub repository access, project versions + sprint
 *                    duration, wallet, refer).
 *      ExiTracker  — the founder's product overview (see below). The older
 *                    project screen (college-intro/tracker-work.jpg) was
 *                    replaced on this page at the founder's request.
 *      ExiHire     — an AI-powered HIRING platform where students find jobs,
 *                    apply and are interviewed (founder, 29-Sep-2026: "not an
 *                    interview practice platform" — interview PRACTICE lives
 *                    in TSG Nexus mock interviews). The report screen is
 *                    public/college-intro/exihire-report.jpg:
 *                    AI Coding / AI Technical / AI HR rounds scored out of
 *                    100, score breakdown, integrity/proctoring, key
 *                    strengths, overall evaluation, downloadable report.
 *  · Referral percentages are NEVER written here. They come from
 *    GET /api/nextgen-builder/billing/referral-program (Super Admin →
 *    Feature Pricing). Seeded at 5 % (friend) and 3 % (referrer) on
 *    03/04-Sep-2026, but an admin can change them at any time.
 *  · No guaranteed job, placement, interview, salary or timeline. No student
 *    counts, company logos, testimonials or statistics — none are verified.
 *  · ExiTracker capabilities (29-Sep-2026) also come from the founder's own
 *    product overview public/student-intro/exitracker-overview.* (work-item
 *    hierarchy Backlog→Epic→Feature→User Story→Task→Sub Task→Bug→…→Release,
 *    sprint/release burndown, velocity, boards, dependencies, milestones).
 *    Its numbers are illustrative sample data — label it as an overview,
 *    never as a live project.
 *  · Git: TSG Nexus asks for the student's public GitHub handle and invites it
 *    to the official project repository (TenantGitHubProfile, NextGenEnrollment,
 *    NextGenProjectVersion). Project repositories themselves may be PRIVATE —
 *    the screenshot shows one — so never claim every repo is public.
 *  · Multilingual (30-Sep-2026): Elite Mastery language switching
 *    (components/elite/EliteLanguageSelector.jsx, backend eliteMultilingual +
 *    contentLocalizationEngine) and AI Tutor language choice, over the 23
 *    options in components/aiTutor/languages.js. Code/commands/URLs are
 *    protected from translation by placeholders. The free-trial page count is
 *    configuration (billing.elite_multilingual_free_pages) — never print it.
 *  · Product links are the founder's (29-Sep-2026): ExiHire https://exihire.com/
 *    (job finding — never describe it as practice), ExiTracker
 *    https://www.exitracker.com/.
 *  · Evolve X opportunity (founder, 29-Sep-2026): the 3 best performers who
 *    take TSG Nexus and build projects get the opportunity to INTERVIEW with
 *    Evolve X Innovations. It is not a job offer — recruitment depends on
 *    their interview performance. Keep that sentence next to the offer.
 */

/* ── Destinations ──────────────────────────────────────────────────
   Absolute on purpose (unchanged from the previous page): the student funnel
   lives on the apex domain even when this page is served from a tenant host. */
const APEX = 'https://www.theskillgenie.com';
export const URLS = {
  register: `${APEX}/nextgen-builder/register`,
  login: `${APEX}/nextgen-builder/login`,
  dashboard: `${APEX}/nextgen-builder/dashboard`,
  refer: `${APEX}/nextgen-builder/refer`,
  // Sister products — external sites, opened in a new tab.
  exihire: 'https://exihire.com/',
  exitracker: 'https://www.exitracker.com/',
};

export const LOGO_SRC = '/college-intro/tsg-logo.png';

/* ── This page's route ─────────────────────────────────────────────
   The official URL. /student-intro is kept only as a redirect to it. */
export const PAGE_PATH = '/tsg-product-engg';

/* ── CTA vocabulary — one label per job, page-wide ─────────────── */
export const CTA = {
  start: 'Start Your TSG Nexus Journey',
  startShort: 'Start Your Journey',
  continue: 'Continue Your Journey',
  explore: 'Explore the Experience',
  signIn: 'Sign in',
  refer: 'Refer a Friend',
  referHow: 'See How Referrals Work',
};

/* ── 1 — Hero ───────────────────────────────────────────────────── */
export const HERO = {
  eyebrow: 'TSG Nexus • Product Engineering',
  h1: [
    { text: 'Build like', tone: 'strong' },
    { text: 'an engineer.', tone: 'strong' },
    { text: 'Think like', tone: 'hot' },
    { text: 'an engineer.', tone: 'hot' },
  ],
  tagline: 'Become Industry-Ready Before You Enter the Industry.',
  lead:
    'TSG Nexus is a product engineering experience where you learn, build real products, work with engineering workflows, and develop the practical ability to contribute from day one.',
  cta: 'Start Your Product Engineering Journey',
  signedInNote: 'You’re signed in — pick up where you left off.',
  // The orbit. Order is the journey, clockwise from the top-left.
  orbit: [
    { id: 'learn', n: '01', name: 'Learn', sub: 'TSG Nexus' },
    { id: 'build', n: '02', name: 'Build', sub: 'Real projects' },
    { id: 'tracker', n: '03', name: 'ExiTracker', sub: 'Engineering work' },
    { id: 'hire', n: '04', name: 'ExiHire', sub: 'Jobs + AI interviews' },
    { id: 'confidence', n: '05', name: 'Confidence', sub: 'Earned, not given' },
  ],
  core: { top: 'TSG', bottom: 'NEXUS' },
  strip: ['Learn', 'Build', 'Engineer', 'Experience', 'Prove'],
};

/* ── 2 — Beyond courses ─────────────────────────────────────────── */
export const JOURNEY = {
  eyebrow: 'Your journey',
  title: 'Your journey goes beyond courses.',
  lead:
    'Most students stop once they have learned a technology. TSG Nexus is built for what comes after — using it, shipping with it, and explaining it under pressure.',
  quote:
    'You don’t build confidence by being told you’re ready. You build it by practising what you’ll actually be expected to do.',
  steps: [
    { id: 'knowledge', name: 'Knowledge', body: 'Structured technology tracks, and an AI tutor that explains a concept until it clicks.' },
    { id: 'practice', name: 'Practice', body: 'Skill tests and coding rounds that score you and show what was weak.' },
    { id: 'projects', name: 'Projects', body: 'A real project, a real GitHub repository, pull requests and AI code review.' },
    { id: 'workflow', name: 'Engineering workflow', body: 'Epics, user stories, tasks, boards and sprints in ExiTracker — the way work actually moves.' },
    { id: 'interviews', name: 'Interviews', body: 'Mock coding, technical and HR rounds in TSG Nexus — then real AI interview rounds when you apply through ExiHire.' },
    { id: 'feedback', name: 'Feedback', body: 'Reports that name your weak areas, so the next attempt is not a repeat of the last.' },
    { id: 'confidence', name: 'Confidence', body: 'The kind that comes from evidence — things you have built, explained and improved.' },
  ],
};

/* ── Product images ────────────────────────────────────────────────
   Every screenshot on the page is declared once here. `sources` are WebP
   widths for srcset; `fallback` is the JPEG for browsers without WebP.
   All are real product material — nothing is mocked up. */
export const IMAGES = {
  github: {
    sources: [['/student-intro/github-repo-800.webp', 800], ['/student-intro/github-repo.webp', 1266]],
    fallback: '/student-intro/github-repo.jpg',
    w: 1266,
    h: 653,
    url: 'github.com/evolvexinnovations/skillgenie-bootcamp-test-engineering-college',
    alt: 'A real TSG Nexus bootcamp repository on GitHub under the evolvexinnovations organisation: the Code tab with the main branch, 2 branches, 2 commits, a .gitignore and README.md, and the README titled “Test Engineering College — SkillGenie Bootcamp Repository”.',
    label: 'Real repository · GitHub',
    full: '/student-intro/github-repo.jpg',
  },
  tracker: {
    sources: [['/student-intro/exitracker-overview-960.webp', 960], ['/student-intro/exitracker-overview.webp', 1536]],
    fallback: '/student-intro/exitracker-overview.jpg',
    w: 1536,
    h: 1024,
    alt: 'The ExiTracker product overview: the delivery flow from Backlog through Epic, Feature, User Story, Task, Sub Task, Bug, Improvement, Spike, Dependency and Milestone to Release; team and execution metrics such as capacity, velocity and work in progress; sprint burndown, release burndown, burnup, cumulative flow and cycle-time charts; quality and engineering-health metrics; and delivery forecasts.',
    label: 'ExiTracker product overview · sample data',
    full: '/student-intro/exitracker-overview.jpg',
  },
  eliteTelugu: {
    sources: [['/student-intro/elite-telugu.webp', 1244]],
    fallback: '/student-intro/elite-telugu.jpg',
    w: 1244,
    h: 437,
    url: 'theskillgenie.com · Elite Technology Mastery · Python Premium',
    alt: 'A real Elite Technology Mastery page — Python Premium, “Numbers and Type Conversion” — switched to Telugu with the Language selector: the title, section headings and explanation are in Telugu while Python terms and code such as integers, floats, 5 and -12 stay in English.',
    label: 'Real Elite Mastery page · Telugu',
  },
  hireReport: {
    sources: [['/student-intro/exihire-report.webp', 900]],
    fallback: '/college-intro/exihire-report.jpg',
    w: 900,
    h: 1341,
    alt: 'An ExiHire interview report with the applicant’s name redacted: AI Coding 82/100 and AI HR 78/100, tabs for AI Coding, AI Technical and AI HR details, an 82% overall performance ring, a score breakdown across communication, SQL, problem solving and integrity, key strengths, and an overall evaluation.',
    label: 'Real report · name redacted',
  },
};

/* ── 3 — What industry expects / what TSG Nexus delivers ───────────
   Left side: broad, established engineering expectations. Right side: only
   shipped capabilities. `evidence` names an IMAGES key, or null for a
   text-only proof. */
export const INDUSTRY = {
  eyebrow: 'Expectation → experience',
  title: ['What industry expects.', 'What TSG Nexus delivers.'],
  lead:
    'Industry doesn’t only expect you to know a programming language. It expects you to show how you work. TSG Nexus gives you practical exposure to workflows commonly used in modern engineering teams.',
  rows: [
    { id: 'code', expects: 'Write code', delivers: 'Learn + Build', where: 'TSG Nexus', proof: 'Technology Mastery tracks, an AI tutor, skill tests and AI code review on the code you write.', evidence: null, anchor: 'sx-nexus-title' },
    { id: 'fundamentals', expects: 'Understand the fundamentals deeply', delivers: 'Learn in your language', where: 'TSG Nexus', proof: 'Elite Technology Mastery pages in Telugu, Hindi, Tamil and 20 more languages, and a TSG AI Tutor chat that answers in yours. Code stays exactly as written.', evidence: 'eliteTelugu', anchor: 'sx-lang-title' },
    { id: 'git', expects: 'Use version control', delivers: 'Git-based project experience', where: 'GitHub', proof: 'Your GitHub account is invited to a real project repository. You commit, branch and open pull requests.', evidence: 'github', anchor: 'sx-git-title' },
    { id: 'project', expects: 'Understand how project work moves', delivers: 'ExiTracker', where: 'ExiTracker', proof: 'Epics, features, user stories, tasks and releases — planned, tracked and delivered.', evidence: 'tracker', anchor: 'sx-tracker-title' },
    { id: 'tasks', expects: 'Work with tasks and priorities', delivers: 'Structured work management', where: 'ExiTracker', proof: 'Tasks and sub-tasks with owners, status and priority on boards — plus blocked work, dependencies and milestones.', evidence: null, anchor: 'sx-tracker-title' },
    { id: 'collab', expects: 'Collaborate in a team', delivers: 'Team and repository workflows', where: 'TSG Nexus + GitHub', proof: 'Enrol in a project with an engineering role, work in a shared repository, and get your pull requests reviewed.', evidence: null, anchor: 'sx-git-title' },
    { id: 'explain', expects: 'Explain your technical thinking', delivers: 'Mock interviews', where: 'TSG Nexus', proof: 'Coding, technical, HR, aptitude and time-management rounds, each with a report.', evidence: null, anchor: 'sx-mock-title' },
    { id: 'hire', expects: 'Face real hiring processes', delivers: 'ExiHire — AI-powered hiring', where: 'ExiHire', proof: 'Discover jobs, apply, and go through AI interview rounds with employers.', evidence: 'hireReport', anchor: 'sx-hire-title' },
  ],
};

/* ── 5 — Git / GitHub ───────────────────────────────────────────── */
export const GIT = {
  eyebrow: 'Git & GitHub · Show your work',
  title: ['Build it.', 'Commit it.', 'Show it.'],
  lead:
    'Modern engineering is not only about what you know. It is also about what you build, how you work, and what you can demonstrate.',
  body:
    'In TSG Nexus you add your public GitHub handle, and your account is invited to the official repository for your project. From there you work the way engineering teams do — and every commit and pull request carries your name.',
  points: [
    { id: 'repo', title: 'A real repository', body: 'Not a sandbox: your project lives on GitHub.' },
    { id: 'history', title: 'Your own history', body: 'Commits and pull requests tied to your GitHub account.' },
    { id: 'review', title: 'Reviewed work', body: 'Pull requests are read — by AI code review and mentors.' },
  ],
  flowLabel: 'How code moves in industry — and in your TSG Nexus project',
  flow: ['Code', 'Repository', 'Commits', 'Branches', 'Pull requests', 'Review', 'Delivery'],
  note: 'Project repositories can be private to the team, as in this one. Your contributions still sit in your own GitHub account.',
};

/* ── 7 — Mock interviews (practice lives HERE, never in ExiHire) ─── */
export const MOCK = {
  eyebrow: 'Mock interviews · TSG Nexus',
  title: ['Practise before', 'the real interview.'],
  lead:
    'Knowing how to build something is one skill. Explaining your decisions and solving problems under pressure is another. TSG Nexus lets you rehearse both, as often as you need.',
  rounds: [
    { id: 'coding', n: '01', name: 'Coding', body: 'Solve problems against the clock.' },
    { id: 'technical', n: '02', name: 'Technical', body: 'Explain concepts and your design choices.' },
    { id: 'hr', n: '03', name: 'HR', body: 'Talk about yourself and your work with clarity.' },
    { id: 'aptitude', n: '04', name: 'Aptitude', body: 'Arithmetic and aptitude questions, scored.' },
    { id: 'time', n: '05', name: 'Time management', body: 'A dedicated time-management round.' },
  ],
  after: 'Every round ends with a report, so your next attempt fixes what the last one exposed.',
};

/* ── 3b — The difference: a conventional path vs TSG Nexus ─────────
 * Structural, never competitive: it names no platform, college or course
 * provider, and claims no one else lacks anything. Each `nexus` row is a
 * shipped capability (AI Tutor explain / code-review / resume-review routes,
 * Elite Technology Mastery, project enrolment with GitHub repository access,
 * ExiTracker, /nextgen-builder/mock-interview, ExiHire). "Resume review", not
 * "resume builder": only an AI resume REVIEW exists. Bootcamps are a college
 * programme, not part of TSG Nexus, so they are not listed here.
 *
 * PRICE: ₹9,999 is the TSG Nexus Starter plan in plan_master (GST added at
 * checkout — hence "+ GST"). The plans API needs a signed-in builder, so the
 * figure is copy by the founder's decision (30-Sep-2026): keep it in step
 * with plan_master. It is shown as an entry point, never compared with
 * anyone else's price, and no saving is ever quantified. */
export const DIFFERENCE = {
  eyebrow: 'The difference',
  title: ['Stop collecting learning.', 'Start building experience.'],
  lead:
    'Traditional learning often means combining multiple tools, platforms and disconnected experiences. TSG Nexus brings the learning, building and engineering experience together in one connected journey.',
  oldHead: { kicker: 'The old way', title: 'A path you assemble yourself' },
  nexusHead: { kicker: 'The TSG Nexus experience', title: 'One connected journey' },
  rows: [
    {
      id: 'learn',
      old: { text: 'Learn from a course', where: 'A course' },
      verb: 'Learn',
      name: 'Elite Technology Mastery + TSG AI Tutor',
      chips: ['Elite content', 'AI tutor chat', 'In your language'],
    },
    {
      id: 'build',
      old: { text: 'Find a project to build', where: 'On your own' },
      verb: 'Build',
      name: 'A real TSG Nexus project, in an engineering role',
      chips: ['Project enrolment', 'Versions & sprints'],
    },
    {
      id: 'review',
      old: { text: 'Find someone to review your code', where: 'Somewhere else' },
      verb: 'Review',
      name: 'AI code review on the code you write',
      chips: ['AI code review', 'Pull-request reviews'],
    },
    {
      id: 'flows',
      old: { text: 'Learn project workflows separately', where: 'Another tool' },
      verb: 'Work through engineering flows',
      name: 'ExiTracker — the workflows used to structure and manage software projects',
      chips: ['Tasks & work items', 'Progress', 'Delivery'],
    },
    {
      id: 'practise',
      old: { text: 'Practise interviews somewhere else', where: 'Another platform' },
      verb: 'Practise',
      name: 'Mock interviews, with a report after every round',
      chips: ['Coding', 'Technical', 'HR', 'Aptitude'],
    },
    {
      id: 'show',
      old: { text: 'Keep your code in scattered places', where: 'On your own' },
      verb: 'Show your work',
      name: 'Git-based proof of work you can show',
      chips: ['GitHub repository', 'Commits & pull requests'],
    },
    {
      id: 'prepare',
      old: { text: 'Prepare your resume with another tool', where: 'Another tool' },
      verb: 'Prepare',
      name: 'AI resume review, backed by the work you built',
      chips: ['Resume review'],
    },
    {
      id: 'connect',
      old: { text: 'Search for hiring opportunities', where: 'A separate journey' },
      verb: 'Connect',
      name: 'ExiHire — an AI-powered, real-time hiring ecosystem',
      chips: ['Find jobs', 'AI interview rounds'],
    },
  ],
  oldResult: 'Multiple places to manage the journey',
  nexusResult: 'One connected student experience',
  shift: {
    before: 'Learning is only the beginning.',
    after: 'Experience is what turns learning into engineering capability.',
    from: 'Learning technology',
    to: 'Experiencing how engineering works',
  },
  value: {
    kicker: 'One connected experience',
    product: 'TSG Nexus',
    price: '₹9,999',
    tax: '+ GST',
    body: 'One connected ecosystem instead of piecing together multiple learning and career-preparation experiences.',
  },
  place: {
    kicker: 'Build from where you are',
    title: 'Your location shouldn’t decide your access to engineering experience.',
    body: 'Learn, build and practise from home — without making relocation a prerequisite for the TSG Nexus experience.',
    note: 'This can reduce the need to travel or relocate just to access training, depending on your circumstances.',
  },
  close: ['Don’t just learn what engineers use.', 'Experience how engineers work.'],
  cta: 'Explore the TSG Nexus Experience',
};

/* ── 4 — Learn + Build inside TSG Nexus ─────────────────────────── */
export const NEXUS = {
  eyebrow: 'TSG Nexus · Learn + Build',
  title: 'Start with the technology. Then build with it.',
  lead: 'Everything here lives in your TSG Nexus workspace, and it remembers where you left off.',
  columns: [
    {
      id: 'learn',
      verb: 'Learn',
      items: [
        { name: 'Technology Mastery tracks', body: 'A structured path through a technology — fundamentals first, then the parts interviews ask about.' },
        { name: 'Learn in your language', body: 'Switch Elite Technology Mastery pages into Telugu, Hindi, Tamil and 20 more languages. Code stays exactly as written.' },
        { name: 'TSG AI Tutor chat', body: 'Ask anything and get deep explanations in the language you pick, plus notes you can come back to.' },
        { name: 'Skill tests', body: 'Find out what you actually know before an interviewer does.' },
        { name: 'Resume review', body: 'Honest feedback on the page that decides whether anyone reads the rest.' },
      ],
    },
    {
      id: 'build',
      verb: 'Build',
      items: [
        { name: 'A real project, a real role', body: 'Enrol in a project and choose the engineering role you want to play in it.' },
        { name: 'GitHub repository access', body: 'Commit early, push often, open pull requests — the way teams ship.' },
        { name: 'AI code review', body: 'Specific feedback on the code you wrote: what is fragile, what is unclear.' },
        { name: 'Versions and sprints', body: 'Project versions and sprint timelines tracked on your dashboard.' },
      ],
    },
  ],
  // Multilingual learning: Elite Technology Mastery language switching and
  // the TSG AI Tutor chat's language picker (ExplainPage + LanguagePicker).
  // NOT claimed for the Tee Gee visitor guide — it has no language setting.
  // The list mirrors components/aiTutor/languages.js — the one language
  // list the product uses; 23 options including English. Native names are
  // shown so a student recognises their own language at a glance.
  languages: {
    eyebrow: 'Multilingual · Elite Mastery + TSG AI Tutor',
    title: 'Understand it in your language. Build it in the language of the industry.',
    body:
      'A concept that will not click in English often clicks in your own. Switch any Elite Technology Mastery page into your language, and chat with the TSG AI Tutor in it — every answer comes back in the language you pick. Then build and interview in English, the way the industry works.',
    note: 'Code, commands and technical terms are never translated — they stay exactly as you will use them.',
    list: [
      { code: 'hi', native: 'हिन्दी', name: 'Hindi' },
      { code: 'te', native: 'తెలుగు', name: 'Telugu' },
      { code: 'ta', native: 'தமிழ்', name: 'Tamil' },
      { code: 'kn', native: 'ಕನ್ನಡ', name: 'Kannada' },
      { code: 'ml', native: 'മലയാളം', name: 'Malayalam' },
      { code: 'mr', native: 'मराठी', name: 'Marathi' },
      { code: 'gu', native: 'ગુજરાતી', name: 'Gujarati' },
      { code: 'bn', native: 'বাংলা', name: 'Bengali' },
      { code: 'pa', native: 'ਪੰਜਾਬੀ', name: 'Punjabi' },
      { code: 'ur', native: 'اردو', name: 'Urdu', rtl: true },
      { code: 'or', native: 'ଓଡ଼ିଆ', name: 'Odia' },
      { code: 'as', native: 'অসমীয়া', name: 'Assamese' },
    ],
    more: '+ 10 international languages',
  },
};

/* ── 6 — ExiTracker ─────────────────────────────────────────────── */
export const TRACKER = {
  eyebrow: 'ExiTracker · Experience engineering work',
  title: ['Don’t just build projects.', 'Experience how engineering work moves.'],
  lead:
    'Students don’t just need to know how to code. They need to understand how engineering work is planned, organised, tracked and delivered. ExiTracker is a Jira-style work-management platform — and part of your TSG Nexus experience.',
  // Callouts pinned to regions of the overview image. `x`/`y` are % of the
  // image; each names a panel that is visibly in the image.
  callouts: [
    { id: 'flow', label: 'Work-item hierarchy: epic → story → task', x: 3.5, y: 9.6 },
    { id: 'team', label: 'Team capacity & velocity', x: 3.5, y: 23.4 },
    { id: 'sprint', label: 'Sprint & release burndown', x: 66.5, y: 23.4 },
    { id: 'quality', label: 'Quality & code review health', x: 3.5, y: 59.8 },
  ],
  pillars: [
    { id: 'plan', verb: 'Plan', body: 'Break work into epics, features, user stories, tasks and sub-tasks. Set milestones and releases.' },
    { id: 'track', verb: 'Track', body: 'Boards, status, priorities, blocked work and sprint burndown — progress you can see.' },
    { id: 'collaborate', verb: 'Collaborate', body: 'Assignments, team capacity and dependencies, with meetings and a knowledge hub alongside the work.' },
    { id: 'deliver', verb: 'Deliver', body: 'Releases, milestones and delivery forecasts — from idea to shipped.' },
  ],
  // The work-item hierarchy, straight from the product overview.
  hierarchy: ['Backlog', 'Epic', 'Feature', 'User Story', 'Task', 'Sub Task', 'Release'],
  hierarchyCaption:
    'You don’t just submit an assignment. You experience how structured engineering work is planned, tracked and delivered.',
  whyTitle: 'Why this matters',
  why: [
    { id: 'git', instead: 'Instead of only learning Git…', now: 'you see how work moves through a team — from backlog to review to release.' },
    { id: 'reqs', instead: 'Instead of only building assignments…', now: 'you work against epics, user stories, tasks and priorities.' },
    { id: 'claim', instead: 'Instead of only saying “I worked on a project”…', now: 'you can explain the workflow behind delivering it.' },
    { id: 'theory', instead: 'Instead of only learning theory…', now: 'you practise a process closer to how engineering teams operate.' },
  ],
};

/* ── 8 — ExiHire ────────────────────────────────────────────────── */
export const HIRE = {
  eyebrow: 'ExiHire · Real-time AI hiring',
  title: ['Build it.', 'Then find the job for it.'],
  lead:
    'ExiHire is an AI-powered hiring platform. Discover jobs, apply, and go through AI interview rounds where employers see what you can actually do — not just what your resume says.',
  points: [
    { id: 'jobs', title: 'Find jobs', body: 'Discover openings and apply from one place.' },
    { id: 'rounds', title: 'AI interview rounds', body: 'AI Coding, AI Technical and AI HR rounds, each scored out of 100.' },
    { id: 'breakdown', title: 'Assessed on skill', body: 'Communication, problem solving and technical depth scored separately, with named strengths.' },
    { id: 'report', title: 'A clear outcome', body: 'An overall evaluation and next-round recommendation, in a report you can download.' },
  ],
  // How a job search moves on ExiHire. No practice steps here — ever.
  loop: ['Discover jobs', 'Apply', 'AI interview', 'Evaluation', 'Next round', 'Employer decision'],
  loopCentre: 'ExiHire',
  statement: 'Your skills, in front of employers.',
  promise:
    'ExiHire helps you find opportunities and be assessed fairly on what you can do. Hiring decisions stay with employers — no platform can promise you a job, and we don’t.',
};

/* ── 9 — From code to delivery ─────────────────────────────────── */
export const FLOW = {
  eyebrow: 'One journey',
  title: 'From code to delivery.',
  lead: 'Not separate tools you sign into one by one — one path, where each experience hands you to the next.',
  stages: [
    { id: 'git', name: 'Git & GitHub', pair: 'Show what you build', stop: 'Commits and pull requests in a real repository.' },
    { id: 'tracker', name: 'ExiTracker', pair: 'Understand how work gets delivered', stop: 'Epics, stories, tasks, sprints and releases.' },
    { id: 'mock', name: 'Mock interviews', pair: 'Learn to explain what you built', stop: 'Coding, technical and HR rounds in TSG Nexus.' },
    { id: 'hire', name: 'ExiHire', pair: 'Real-time AI hiring', stop: 'Discover jobs, apply, and interview with employers.' },
  ],
  outcome: [
    { id: 'portfolio', name: 'Portfolio & experience', body: 'Show what you built and how you built it.' },
    { id: 'confidence', name: 'Confidence', body: 'Step forward with evidence behind you.' },
  ],
};

/* ── 10 — Evolve X opportunity ─────────────────────────────────────
   Wording is load-bearing: an INTERVIEW opportunity, never a job offer. */
export const OPPORTUNITY = {
  eyebrow: 'The Evolve X opportunity',
  title: ['Build your best work.', 'Earn an interview with us.'],
  lead:
    'From the students who take TSG Nexus and build projects, we pick the 3 best performers and give them the opportunity to interview with Evolve X Innovations — the company behind TSG Nexus and ExiHire.',
  steps: [
    { id: 'build', n: '01', title: 'Build on TSG Nexus', body: 'Complete your projects and do your best work.' },
    { id: 'select', n: '02', title: 'Top 3 performers selected', body: 'Chosen on how they performed in TSG Nexus and the projects they built.' },
    { id: 'interview', n: '03', title: 'Interview with Evolve X', body: 'An interview opportunity at Evolve X Innovations.' },
    { id: 'decide', n: '04', title: 'Recruitment on merit', body: 'Whether you are recruited depends on how you perform in that interview.' },
  ],
  note:
    'This is an interview opportunity, not a job offer. Selection and recruitment are both based on performance.',
};

/* ── 11 — Referral ───────────────────────────────────────────────── */
export const REFERRAL = {
  eyebrow: 'Refer & earn',
  title: ['Learn together.', 'Earn together.'],
  lead: 'Know someone who wants to build real skills? Invite them to TSG Nexus. They get a discount, and you can earn a referral reward.',
  steps: [
    { id: 'share', n: '01', title: 'Share', body: 'Share your TSG Nexus referral link or code.' },
    { id: 'join', n: '02', title: 'Your friend joins', body: 'They use your code at checkout on an eligible subscription.' },
    { id: 'earn', n: '03', title: 'You earn', body: 'Once their payment is successfully verified, your reward is credited to your TSG Nexus wallet.' },
  ],
  nodes: { a: 'You', b: 'Your friend', hub: 'TSG Nexus' },
  // Rules the existing Refer & Earn page states — shown in the disclosure.
  rules: [
    'The discount applies once per account, before tax.',
    'Credit packs are not discounted.',
    'Your reward is a share of the eligible subscription amount after the referral discount, before tax.',
    'Rewards are credited only after the payment is verified, and can be withdrawn like any other wallet earnings.',
  ],
  note: 'Percentages and eligible products are set by TSG Nexus and may change. The Refer & Earn page always shows the current terms.',
};

/* ── 12 — Confidence ─────────────────────────────────────────────── */
export const CONFIDENCE = {
  eyebrow: 'Confidence',
  title: ['Confidence is built.', 'Not given.'],
  lines: [
    'You don’t need to know everything before you begin.',
    'You need a place where you can learn, build, make mistakes, debug, practise, receive feedback — and improve.',
  ],
  terms: ['Knowledge', 'Practice', 'Projects', 'Engineering experience', 'Interview practice', 'Feedback'],
  result: 'Confidence',
  statement: 'We don’t promise you a job. We help you build the evidence that you can do the job.',
};

/* ── 13 — Close ─────────────────────────────────────────────────── */
export const CLOSE = {
  title: 'Your engineering journey starts here.',
  verbs: ['Learn.', 'Build.', 'Experience.', 'Practise.', 'Prove.'],
  partnerNote:
    'Studying at a partner college? Your institution may already have given you an account — check with your placement cell before registering separately.',
};

/* ── SEO ────────────────────────────────────────────────────────── */
export const SEO = {
  title: 'TSG Nexus Product Engineering | The Skill Genie',
  description:
    'TSG Nexus: learn a technology, build real products on GitHub, work through engineering workflows and practise interviews before you enter the industry.',
  keywords:
    'TSG Nexus, product engineering, student engineering experience, learn in your language, project-based learning, software engineering workflows, Git, ExiTracker, mock interviews, ExiHire, industry readiness',
  canonical: 'https://www.theskillgenie.com/tsg-product-engg',
  ogTitle: 'TSG Nexus Product Engineering — Build like an engineer. Think like an engineer.',
  ogDescription:
    'Become industry-ready before you enter the industry. Learn, build real products, work with engineering workflows, practise interviews and find opportunities on ExiHire.',
  ogImage: 'https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/TSG_Logo.png',
};
